"use client";
// HMR Update

import { PAID_PLAN_TIERS } from "@repo/config";
import { useClarityAnalytics, useUmamiAnalytics } from "@analytics";
import { authClient } from "@repo/auth/client";
import { config } from "@repo/config";
import { useLoginModal } from "@saas/auth/components/LoginModalProvider";
import { setAuthHint } from "@saas/auth/lib/auth-hint";
import { useSession } from "@saas/auth/hooks/use-session";
import { UserAvatar } from "@shared/components/UserAvatar";
import { useRouter } from "@shared/hooks/router";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import { cn } from "@ui/lib";
import {
	ChevronsUpDown,
	CreditCard,
	ImagesIcon,
	LogIn,
	LogOutIcon,
	type LucideIcon,
	SettingsIcon,
	Sparkles,
	Coins,
	Zap,
} from "lucide-react";
import { useCredits } from "@saas/credits/hooks/use-credits";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { useTranslations } from "next-intl";
import { LocaleLink } from "@i18n/routing";
import { usePathname } from "next/navigation";
import { useState } from "react";
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetTitle,
	SheetTrigger,
} from "@ui/components/sheet";

export function UserMenu({
	showUserName,
	className,
	side = "bottom",
	align = "end",
	sideOffset = 4,
	mobileSheet = false,
}: {
	showUserName?: boolean;
	className?: string;
	side?: "top" | "right" | "bottom" | "left";
	align?: "start" | "center" | "end";
	sideOffset?: number;
	/** 营销页 navbar 用：lg 以下改成底部抽屉 */
	mobileSheet?: boolean;
}) {
	const t = useTranslations("subscription");
	const appT = useTranslations("app");
	const commonT = useTranslations("common");
	const { user } = useSession();
	const { showLoginModal } = useLoginModal();
	const { getRemainingCredits, getTotalCredits, hasUnlimitedCredits } =
		useCredits();
	const pathname = usePathname();
	const router = useRouter();
	const { hasSubscription } = usePurchases();
	const isPaidPlan = hasSubscription([...PAID_PLAN_TIERS]);
	const { trackEvent: trackUmamiEvent } = useUmamiAnalytics();
	const { trackEvent: trackClarityEvent } = useClarityAnalytics();

	const [sheetOpen, setSheetOpen] = useState(false);

	const onLogout = () => {
		authClient.signOut({
			fetchOptions: {
				onSuccess: () => {
					// 跳转后的营销页首帧别再按「已登录」藏东西
					setAuthHint(false);
					// 别先 await clearCache()：它的回包带着当前页的新 RSC 流，
					// 紧跟着的整页跳转会把流掐断，iOS / Firefox 因此进全局错误页，
					// 自动刷新还把去首页的跳转盖掉。整页加载本来就拿新数据。
					window.location.href = new URL(
						config.auth.redirectAfterLogout,
						window.location.origin,
					).toString();
				},
			},
		});
	};

	if (!user) {
		const startForFreeLabel = appT("userMenu.startForFree");
		return (
			<button
				type="button"
				onClick={() => showLoginModal(pathname)}
				className={cn(
					"flex items-center gap-2 rounded-md outline-hidden focus-visible:ring-2 focus-visible:ring-primary",
					showUserName ? "w-full p-2" : "p-0.5",
					"hover:bg-primary/5",
					className,
				)}
				aria-label={startForFreeLabel}
			>
				<span className="flex items-center gap-2">
					<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
						<LogIn className="size-4" />
					</div>
					{showUserName && (
						<span className="text-left font-semibold text-sm">
							{startForFreeLabel}
						</span>
					)}
				</span>
			</button>
		);
	}

	const { name, email, image } = user;

	// 付费用户点这些是来看余额、管订阅，不是升级；只有免费用户点才算升级意向
	const trackUpgrade = (location: string) => {
		if (!isPaidPlan) {
			void trackUmamiEvent("upgrade-clicked", { location });
			trackClarityEvent("upgrade-clicked");
		}
	};

	// 下拉菜单和手机底部抽屉共用一份条目；第一行是余额，后面跟分隔线
	const links: {
		href: string;
		icon: LucideIcon;
		label: string;
		value?: number | string;
		onClick?: () => void;
	}[] = [
		{
			href: "/subscription",
			icon: Zap,
			label: appT("userMenu.credits"),
			value: hasUnlimitedCredits("IMAGE_GENERATION")
				? "∞"
				: getRemainingCredits("IMAGE_GENERATION"),
			onClick: () => trackUpgrade("user-menu-credits"),
		},
		{
			href: "/my-assets",
			icon: ImagesIcon,
			label: appT("userMenu.myAssets"),
		},
		{
			href: "/settings",
			icon: SettingsIcon,
			label: appT("userMenu.settings"),
		},
		{
			href: "/subscription",
			icon: CreditCard,
			label: t("tabs.mySubscription"),
			onClick: () => trackUpgrade("user-menu"),
		},
	];
	const [creditsLink, ...navLinks] = links;

	const renderLinkBody = (link: (typeof links)[number]) => (
		<>
			<link.icon className="mr-2 size-4 shrink-0" />
			<span className="flex-1">{link.label}</span>
			{link.value !== undefined && (
				<span className="ml-4 font-semibold tabular-nums">
					{link.value}
				</span>
			)}
		</>
	);

	const renderTrigger = (extraClassName?: string) => (
		<button
			type="button"
			className={cn(
				"flex items-center gap-2 rounded-md outline-hidden focus-visible:ring-2 focus-visible:ring-primary",
				showUserName ? "w-full p-2" : "p-0.5",
				"hover:bg-primary/5",
				className,
				extraClassName,
			)}
			aria-label="User menu"
		>
			<span className="flex items-center gap-2">
				<UserAvatar name={name ?? ""} avatarUrl={image} />
				{showUserName && (
					<span className="text-left flex flex-col gap-0.5 min-w-0">
						<span className="font-semibold text-sm truncate text-foreground">
							{name}
						</span>
						<span className="text-[10px] opacity-50 truncate max-w-[150px]">
							{email}
						</span>
					</span>
				)}
			</span>

			{showUserName && <ChevronsUpDown className="size-4 opacity-50" />}
		</button>
	);

	const dropdown = (
		<DropdownMenu modal={false}>
			<DropdownMenuTrigger asChild>{renderTrigger()}</DropdownMenuTrigger>

			<DropdownMenuContent
				side={side}
				align={align}
				sideOffset={sideOffset}
			>
				<DropdownMenuLabel>
					{name}
					<span className="block font-normal text-xs opacity-70">
						{email}
					</span>
				</DropdownMenuLabel>

				<DropdownMenuSeparator />

				<DropdownMenuItem asChild>
					<LocaleLink
						href={creditsLink.href}
						onClick={creditsLink.onClick}
					>
						{renderLinkBody(creditsLink)}
					</LocaleLink>
				</DropdownMenuItem>

				<DropdownMenuSeparator />

				{navLinks.map((link) => (
					<DropdownMenuItem key={link.label} asChild>
						<LocaleLink href={link.href} onClick={link.onClick}>
							{renderLinkBody(link)}
						</LocaleLink>
					</DropdownMenuItem>
				))}

				<DropdownMenuSeparator />

				<DropdownMenuItem onClick={onLogout}>
					<LogOutIcon className="mr-2 size-4" />
					{appT("userMenu.logout")}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);

	if (!mobileSheet) {
		return dropdown;
	}

	// 手机（lg 以下）从底部弹抽屉，拇指够得着；桌面还是头像下拉
	const sheetRowClass =
		"flex min-h-12 w-full items-center rounded-xl px-3 text-left text-base hover:bg-muted [&>svg]:mr-3 [&>svg]:size-5";
	return (
		<>
			<div className="hidden lg:block">{dropdown}</div>
			<Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
				<SheetTrigger asChild>
					{renderTrigger("lg:hidden")}
				</SheetTrigger>
				<SheetContent
					side="bottom"
					aria-describedby={undefined}
					className="rounded-t-3xl px-4 pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] lg:hidden"
				>
					<div className="flex items-center gap-3 px-3 pb-4">
						<UserAvatar
							name={name ?? ""}
							avatarUrl={image}
							className="size-11"
						/>
						<div className="min-w-0">
							<SheetTitle className="truncate text-base">
								{name}
							</SheetTitle>
							<p className="truncate text-muted-foreground text-sm">
								{email}
							</p>
						</div>
					</div>
					<SheetClose asChild>
						<LocaleLink
							href={creditsLink.href}
							onClick={creditsLink.onClick}
							className={sheetRowClass}
						>
							{renderLinkBody(creditsLink)}
						</LocaleLink>
					</SheetClose>
					<div className="mx-3 my-2 h-px bg-border" />
					{navLinks.map((link) => (
						<SheetClose key={link.label} asChild>
							<LocaleLink
								href={link.href}
								onClick={link.onClick}
								className={sheetRowClass}
							>
								{renderLinkBody(link)}
							</LocaleLink>
						</SheetClose>
					))}
					<div className="mx-3 my-2 h-px bg-border" />
					<button
						type="button"
						onClick={onLogout}
						className={sheetRowClass}
					>
						<LogOutIcon className="mr-2 size-4" />
						{appT("userMenu.logout")}
					</button>
				</SheetContent>
			</Sheet>
		</>
	);
}
